import { ModalButtonStyles, OpenModal, useTable } from '@composables/common'
import NameAndId from '@src/components/NameAndId.vue'
import UpdateOrCreatePlatformAcceptanceDocumentForm from '@src/partials/platforms/UpdateOrCreatePlatformAcceptanceDocumentForm.vue'
import { PlatformAcceptanceDocument, PlatformAttachment } from '@src/types'
import { uploadPrivate } from '@src/utils/api/s3'
import { useLanguageStore } from '@src/utils/stores/languages'
import { useUserStore } from '@src/utils/stores/user'
import { computed, h, ref, Ref } from 'vue'
import { useI18n } from 'vue-i18n'

export function usePlatformAcceptanceDocumentsTable(
    acceptanceDocuments: Ref<PlatformAcceptanceDocument[]>,
    update: (successMessage?: string | null) => Promise<void>,
    openModal: OpenModal,
    isModalOpen: Ref<boolean>
) {
    const userStore = useUserStore()
    const i18n = useI18n()
    const languageStore = useLanguageStore()

    const {
        TableWrapper: PlatformAcceptanceDocumentsTable,
        selected: selectedPlatformAcceptanceDocumentsIds,
    } = useTable({
        tableProps: {
            id: 'platform_acceptance_documents',
            label: 'platform_acceptance_documents',
            help: 'help_platform_acceptance_documents_table',
            data: acceptanceDocuments,
            total: acceptanceDocuments.value.length,
            columns: [
                {
                    label: 'id',
                    key: 'id',
                    select: true,
                },
                {
                    label: 'id',
                    key: 'id',
                    condition: userStore.can('see_id_columns'),
                    copy: true,
                },
                {
                    label: 'name',
                    key: 'name',
                },
                {
                    label: 'language',
                    key: 'language',
                    cellRenderer: (props: { rowData: PlatformAttachment }) => {
                        return (
                            languageStore.languages.find(
                                (language) =>
                                    language.value === props.rowData.language
                            )?.label || props.rowData.language
                        )
                    },
                },
            ],
            actions: [
                {
                    action: handleViewPlatformAcceptanceDocument,
                    icon: 'fa-eye',
                    title: 'view',
                },
                {
                    action: handleUpdateOrCreatePlatformAcceptanceDocument,
                    icon: 'fa-solid fa-pen',
                    title: 'edit',
                    condition: userStore.can('update_platform'),
                },
                {
                    action: handleDeletePlatformAcceptanceDocument,
                    icon: 'fas fa-trash-alt',
                    title: 'delete',
                    color: 'text-danger-500',
                    condition: userStore.can('update_platform'),
                },
            ],
        },
    })

    function handleViewPlatformAcceptanceDocument(
        platformAcceptanceDocument: PlatformAcceptanceDocument
    ) {
        const url = platformAcceptanceDocument.url
        if (url) {
            window.open(url)
        }
    }

    function handleUpdateOrCreatePlatformAcceptanceDocument(
        platformAcceptanceDocument: PlatformAcceptanceDocument = new PlatformAcceptanceDocument()
    ) {
        const editedPlatformAcceptanceDocument = ref(
            new PlatformAcceptanceDocument()
        )
        editedPlatformAcceptanceDocument.value = {
            ...platformAcceptanceDocument,
        }
        const file = ref<File | null>(null)
        isModalOpen.value = true

        const disableCreate = computed(() => {
            return !(
                editedPlatformAcceptanceDocument.value.upload_key &&
                editedPlatformAcceptanceDocument.value.language
            )
        })
        openModal(
            {
                id: 'edit_platform_acceptance_document',
                title: editedPlatformAcceptanceDocument.value.id
                    ? 'modal_title_update_platform_acceptance_document'
                    : 'modal_title_create_platform_acceptance_document',
                open: isModalOpen,
                onCancel: () => (isModalOpen.value = false),
                onSubmit: () =>
                    savePlatformAcceptanceDocument(
                        editedPlatformAcceptanceDocument.value,
                        file.value as File
                    ),
            },
            () =>
                h(UpdateOrCreatePlatformAcceptanceDocumentForm, {
                    // eslint-disable-next-line quote-props
                    modelValue: editedPlatformAcceptanceDocument,
                    'onUpdate:modelValue': (
                        value: unknown,
                        files: Ref<FileList>
                    ) => {
                        file.value = files.value?.item(0)
                    },
                }),
            {
                submitButtonText: editedPlatformAcceptanceDocument.value.id
                    ? 'update'
                    : 'create',
                styles: {
                    submitButton: ModalButtonStyles['BLUE'],
                },
                submitButtonDisabled: disableCreate,
            }
        )
    }

    async function savePlatformAcceptanceDocument(
        platformAcceptanceDocument: PlatformAcceptanceDocument,
        file: File
    ) {
        const acceptanceDocumentsTemp = [...acceptanceDocuments.value]
        if (file) {
            const uploadPrivateResponse = await uploadPrivate(file)
            platformAcceptanceDocument.upload_key =
                uploadPrivateResponse.data.key
            platformAcceptanceDocument.url =
                uploadPrivateResponse.data.temp_storage_url
        }

        if (platformAcceptanceDocument.id) {
            acceptanceDocuments.value[
                acceptanceDocuments.value.findIndex(
                    (acceptanceDocument: PlatformAcceptanceDocument) =>
                        platformAcceptanceDocument.id == acceptanceDocument.id
                )
            ] = platformAcceptanceDocument
        } else {
            acceptanceDocuments.value.push(platformAcceptanceDocument)
        }

        let toast = 'toast_success_platform_acceptance_document_updated'
        if (!platformAcceptanceDocument.id) {
            toast = 'toast_success_platform_acceptance_document_created'
        }
        update(
            i18n.t(toast, {
                attachment: platformAcceptanceDocument.name,
            })
        )
            .catch((error) => {
                acceptanceDocuments.value = acceptanceDocumentsTemp
                throw error
            })
            .then(() => {
                isModalOpen.value = false
            })
    }

    function handleDeletePlatformAcceptanceDocument(
        platformAcceptanceDocument: PlatformAcceptanceDocument
    ) {
        isModalOpen.value = true
        openModal(
            {
                id: 'delete_platform_acceptance_document',
                title: 'modal_title_delete_platform_acceptance_document',
                open: isModalOpen,
                onCancel: () => (isModalOpen.value = false),
                onSubmit: () =>
                    deletePlatformAcceptanceDocument(
                        platformAcceptanceDocument
                    ),
            },
            () =>
                h(NameAndId, {
                    idKey: 'id',
                    nameKey: 'name',
                    modelValue: [platformAcceptanceDocument],
                }),
            {
                submitButtonText: 'delete',
                styles: {
                    submitButton: ModalButtonStyles['RED'],
                },
            }
        )
    }

    function deletePlatformAcceptanceDocument(
        platformAcceptanceDocumentToDelete: PlatformAcceptanceDocument,
        submit = true
    ) {
        const acceptanceDocumentsTemp = [...acceptanceDocuments.value]
        acceptanceDocuments.value.splice(
            acceptanceDocuments.value.findIndex(
                (platformAcceptanceDocument) =>
                    platformAcceptanceDocumentToDelete.id ==
                    platformAcceptanceDocument.id
            ),
            1
        )
        if (submit) {
            update(
                i18n.t('toast_success_platform_acceptance_document_deleted', {
                    platformAcceptanceDocument:
                        platformAcceptanceDocumentToDelete.name,
                })
            )
                .catch((error) => {
                    acceptanceDocuments.value = acceptanceDocumentsTemp
                    throw error
                })
                .then(() => {
                    selectedPlatformAcceptanceDocumentsIds.value = []
                    isModalOpen.value = false
                })
        }
    }

    function handleDeletePlatformAcceptanceDocuments() {
        isModalOpen.value = true
        openModal(
            {
                id: 'delete_platform_acceptance_documents',
                title: 'modal_title_delete_platform_acceptance_documents',
                open: isModalOpen,
                onCancel: () => (isModalOpen.value = false),
                onSubmit: () => deletePlatformAcceptanceDocuments(),
            },
            () =>
                h(NameAndId, {
                    idKey: 'id',
                    nameKey: 'name',
                    modelValue: selectedPlatformAcceptanceDocuments.value,
                }),
            {
                submitButtonText: 'delete',
                styles: {
                    submitButton: ModalButtonStyles['RED'],
                },
            }
        )
    }

    function deletePlatformAcceptanceDocuments() {
        const acceptanceDocumentsTemp = [...acceptanceDocuments.value]
        selectedPlatformAcceptanceDocuments.value.forEach(
            (platformAcceptanceDocument) => {
                deletePlatformAcceptanceDocument(
                    platformAcceptanceDocument,
                    false
                )
            }
        )

        update(
            i18n.t('toast_success_platform_acceptance_documents_deleted', {
                acceptanceDocuments: selectedPlatformAcceptanceDocuments.value
                    .map(
                        (platformAcceptanceDocument) =>
                            platformAcceptanceDocument.name
                    )
                    .join(', '),
            })
        )
            .catch((error) => {
                acceptanceDocuments.value = acceptanceDocumentsTemp
                throw error
            })
            .then(() => {
                selectedPlatformAcceptanceDocumentsIds.value = []
                isModalOpen.value = false
            })
    }

    const selectedPlatformAcceptanceDocuments = computed(() => {
        return acceptanceDocuments.value.filter(
            (item: PlatformAcceptanceDocument) =>
                item.id &&
                selectedPlatformAcceptanceDocumentsIds.value.includes(item.id)
        )
    })

    return {
        PlatformAcceptanceDocumentsTable,
        selectedPlatformAcceptanceDocumentsIds,
        handleUpdateOrCreatePlatformAcceptanceDocument,
        handleDeletePlatformAcceptanceDocuments,
    }
}
